import { getConfigForImportedTest, getConfigForMeasuredTest, getConfigForTest } from 'Components/Worksheets/Config';
import { IConfigFunctionArgs } from 'Components/Worksheets/Core';
import { mergeConfig } from 'Components/Worksheets/Core/Helpers/configHelpers';
import { FormatTypes } from 'Lib/Format';
import * as oPath from '../../../../../Lib/Object/Path/Immutable';
import qestIds from '../../../QestIds';
import getDerivedFromMeasurementsConfig from '../../derivedFromMeasurementsConfig';
import getMeasuredUsingConfig from '../../measuredUsingConfig';
import getLabelConfig from './labelConfig';

/**
 * By default, enter hammer face lengths to nearest 0.01 mm
 * However, if hammer is set to IP units (unlikely in labs doing AS testing) respect this and enter hammer face lengths to nearest 0.001 in
 */
const getHammerFaceLengthConfig = (worksheet: any) => {
  const units = oPath.get(worksheet, `data.Equipment.HammerFaceNominalUnits`);
  return {
    format: units === 'Inch-Pound' ? { nearest: 0.001 } : { nearest: 0.01 },
    uom: units === 'Inch-Pound' ? 'in' : 'mm',
  };
};

const mouldVolumeConfig = { uom: 'cm³', format: { nearest: 0.1 } };

const waterAddedPercentConfig = { format: { nearest: 0.1 } };

const getConfig = (prefix: string) =>
  mergeConfig(
    {
      [`${prefix}.Curing.PortionCuring.Duration`]: { uom: 'h', format: { nearest: 0.1 } },
      [`${prefix}.Curing.PortionCuring.TimeBetweenAdditionOfStabiliserAndCompaction`]: {
        uom: 'h',
        format: { nearest: 0.1 },
      },

      [`${prefix}.Preparation.StabiliserAddedAtDateTimeLocal`]: {
        format: { type: FormatTypes.DateTime },
      },

      [`${prefix}.Equipment.MouldVolume`]: mouldVolumeConfig,
      [`${prefix}.Equipment.HammerFaceDiameter`]: (configArgs: IConfigFunctionArgs) =>
        getHammerFaceLengthConfig(configArgs.worksheet),
      [`${prefix}.Equipment.HammerFaceRadius`]: (configArgs: IConfigFunctionArgs) =>
        getHammerFaceLengthConfig(configArgs.worksheet),

      [`${prefix}.TestPortions.MouldVolume`]: mouldVolumeConfig,
      [`${prefix}.TestPortions.WaterAddedTargetPercent`]: { format: { nearest: 1 } },
      [`${prefix}.TestPortions.Curing.Duration`]: { uom: 'h', format: { nearest: 0.1 } },
      [`${prefix}.TestPortions.WaterAddedPercent`]: waterAddedPercentConfig,
      [`${prefix}.TestPortions.Curing.TimeBetweenAdditionOfStabiliserAndCompaction`]: {
        uom: 'h',
        format: { nearest: 0.1 },
      },
      [`${prefix}.TestPortions.WetDensity`]: {
        uom: 't/m³',
        format: { type: FormatTypes.SignificantFigures, precision: 4 },
      },
      [`${prefix}.TestPortions.DryDensity`]: {
        uom: 't/m³',
        format: { type: FormatTypes.SignificantFigures, precision: 4 },
      },
      [`${prefix}.Curing.LiquidLimitSourceRecord`]: { maxLength: 200 },
      [`${prefix}.Curing.AssociatedLiquidLimit.ExternalTest.Results.LiquidLimit`]: { format: { nearest: 1 } },
      [`${prefix}.Curing.LiquidLimit`]: { format: { nearest: 1 } },

      [`${prefix}.Results.MaximumDryDensity`]: { uom: 't/m³', format: { nearest: 0.01 } },
      [`${prefix}.Results.OptimumMoistureContent`]: { format: { nearest: 0.1 } },

      'mddChart.XAxisMin': { format: { nearest: 0.1 } },
      'mddChart.XAxisMajorStep': { format: { nearest: 0.1 } },

      [`${prefix}.TestFractionParticleDensity.ParticleDensity`]: { uom: 't/m³', format: { nearest: 0.01 } },
      [`${prefix}.TestFractionParticleDensity.AssociatedTestFractionParticleDensity.ExternalTest.Results.ParticleDensity`]:
        {
          uom: 't/m³',
          format: { nearest: 0.01 },
        },

      'cwdChart.XAxisMajorStep': waterAddedPercentConfig,
      'cwdChart.XAxisMin': waterAddedPercentConfig,
      [`${prefix}.Curing.AssociatedLiquidLimit.ImportedTest.Results.LiquidLimit`]: { format: { nearest: 0.1 } },
      ...getConfigForImportedTest(
        qestIds.liquidLimit.AS_1289_3_1_1_LiquidLimit.rev2009,
        `${prefix}.Curing.AssociatedLiquidLimit`,
        [qestIds.liquidLimit.AS_1289_3_1_2_LiquidLimit.rev2009]
      ),

      [`${prefix}.Preparation.AssociatedFineMoisture.ExternalTest.Results.MoistureContentPercent`]: {
        format: { nearest: 0.1 },
      },
      [`${prefix}.Preparation.FineMoistureContent`]: { format: { nearest: 0.1 } },
      ...getConfigForMeasuredTest(
        qestIds.oversizeDetermination.AS_1289_5_1_1,
        `${prefix}.Preparation.AssociatedOversizeDetermination`
      ),
      ...getConfigForTest(
        qestIds.moistureContent.AS_1289_2_1_1.rev2005,
        `${prefix}.TestPortions.MoistureDetermination`
      ),
      ...getConfigForTest(
        qestIds.moistureContent.AS_1289_2_1_6.rev2005,
        `${prefix}.TestPortions.MoistureDetermination`
      ),
      [`${prefix}.ImagesAndRemarks.Remarks`]: { maxLength: 255 },
      [`${prefix}.ImagesAndRemarks.InternalRemarks`]: { maxLength: 255 },
    },
    getLabelConfig(`${prefix}`),
    getMeasuredUsingConfig(`${prefix}`),
    getDerivedFromMeasurementsConfig(`${prefix}`),
    {
      [`${prefix}.Equipment.CompactionBalance`]: { defaultReadability: 1, defaultUom: 'g' },
    },
    getConfigForImportedTest(
      qestIds.moistureContent.AS_1289_2_1_1.rev2005,
      `${prefix}.Preparation.AssociatedFineMoisture`,
      [qestIds.moistureContent.AS_1289_2_1_6.rev2005]
    )
  );

export { getConfig };

export default {
  [qestIds.maximumDryDensity.AS_1289_5_1_1.rev2017]: getConfig,
  [qestIds.maximumDryDensity.AS_1289_5_2_1.rev2017]: getConfig,
};
